import { useEffect, useRef, useState } from 'react';
import { GradientMesh } from './GradientMesh';
import { TechPattern } from './TechPattern';
import { Watermark } from './Watermark';

/**
 * Hero background video.
 *
 * The video renders immediately — there is no probe and no loading state, so
 * the mesh never flashes before it. An earlier version issued a HEAD request
 * to check the file existed and rendered the fallback while that request was
 * in flight, which meant every visit showed mesh-then-video. The file is
 * committed to the repo and ships inside the container, so its existence is
 * not in question and there is nothing to check.
 *
 * The poster frame covers the gap before the first video frame decodes: it is
 * the video's own opening frame, so the transition is invisible.
 *
 * The coded mesh remains for three cases only, all decided synchronously on
 * the first render so they never cause a flash either:
 *
 *   - prefers-reduced-motion — a full-screen moving background is precisely
 *     what that setting exists to suppress
 *   - Data Saver — the browser is telling us the user pays for bandwidth
 *   - the video errors or never becomes playable
 */

/** Decided before first paint, so the correct background renders immediately. */
function prefersStatic(): boolean {
  if (typeof window === 'undefined') return false;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData =
    (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  return reduced || saveData;
}

export function HeroVideo({ src = '/videos/hero.mp4', poster = '/videos/hero-poster.jpg' }) {
  // Initialised from the check itself rather than a 'probing' placeholder —
  // that placeholder was what rendered the mesh on every load.
  const [failed, setFailed] = useState(() => prefersStatic());
  const videoRef = useRef<HTMLVideoElement>(null);

  // If the video is present but never becomes playable, demote rather than
  // leaving an empty box behind the headline.
  useEffect(() => {
    if (failed) return;
    const timer = window.setTimeout(() => {
      const v = videoRef.current;
      if (v && v.readyState < 3) setFailed(true);
    }, 6000);
    return () => window.clearTimeout(timer);
  }, [failed]);

  /*
    Pause the video once the hero has scrolled away.

    A playing <video> keeps decoding frames and repainting for the whole
    document height even when it is nowhere near the viewport, which was
    measurably pinning the home page to ~15fps while scrolling. Pausing
    off-screen returns it to 60fps and costs nothing visually — by definition
    nobody can see the frames being skipped.
  */
  useEffect(() => {
    if (failed) return;
    const v = videoRef.current;
    if (!v) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.01 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [failed]);

  if (failed) {
    /*
      The mesh alone is plainer than the footage, so it keeps the tech pattern
      and watermark the video hero drops — there they add texture rather than
      compete with it.
    */
    return (
      <>
        <GradientMesh />
        <TechPattern opacity={0.05} size={150} />
        <Watermark position="hero" scale={0.66} opacity={0.1} />
      </>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden bg-ink" aria-hidden="true">
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        // `auto`, not `metadata`: the video is the hero's background, so it is
        // needed immediately rather than on demand. With `metadata` the poster
        // held longer than it needed to before the first frame arrived.
        preload="auto"
        onError={() => setFailed(true)}
      />
      {/*
        The footage is particles on PURE BLACK while the rest of the site is
        navy, so the black has to be lifted into the palette or the hero reads
        as a hole above the navy sections below it.

        This used to be a full-screen overlay with `mix-blend-mode: screen`.
        That forced the compositor to re-blend the entire viewport on every
        decoded frame, which measured at ~66ms per frame (about 15fps) while
        scrolling the home page — the single largest source of jank on the
        site. The navy lift below is a plain gradient over the video instead:
        visually near-identical, but it composites once rather than blending
        live against moving pixels.
      */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(115deg, rgb(12 35 64 / 0.55) 0%, rgb(23 58 102 / 0.42) 55%, rgb(30 104 194 / 0.30) 100%)',
        }}
      />
      {/*
        Heavier on the right than it used to be. The hero now carries a drawn
        visual over that side, and the footage's particle wave was competing
        with it — two busy elements in one corner. The footage stays as texture
        behind the whole frame instead of being a subject in its own right.
      */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(115deg, rgb(10 30 55 / 0.94) 0%, rgb(12 35 64 / 0.86) 38%, rgb(12 35 64 / 0.74) 68%, rgb(12 35 64 / 0.66) 100%)',
        }}
      />
      {/* Grounds the bottom edge into the section below. */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/3"
        style={{ background: 'linear-gradient(to bottom, transparent, rgb(12 35 64 / 0.85))' }}
      />
    </div>
  );
}
